import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, of, forkJoin } from 'rxjs';
import { environment } from '../../environments/environment';
import { Room, RoomType, RoomDisplay } from '../models/room.model';
import { AvailableRoom } from '../models/booking.model';
import { GalleryImage } from '../shared/components/hotel-gallery-modal/hotel-gallery-modal.component';

/**
 * Servicio para consumir la API de Suntur Quri Wasi
 * Endpoints públicos sin authorization header:
 * - GET /rooms
 * - GET /room-types
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Obtiene todos los tipos de habitación
   * GET /room-types (sin authorization header)
   * Respuesta: Array de RoomType
   */
  getRoomTypes(): Observable<RoomType[]> {
    return this.http.get<RoomType[]>(`${this.apiUrl}/room-types`).pipe(
      map(response => {
        // La API devuelve un array directo según OpenAPI spec
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching room types:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene todas las habitaciones
   * GET /rooms (sin authorization header)
   * Parámetro opcional: tipoHabitacionId para filtrar por tipo
   * Respuesta: Array de Room
   */
  getRooms(tipoHabitacionId?: number): Observable<Room[]> {
    let url = `${this.apiUrl}/rooms`;
    if (tipoHabitacionId) {
      url += `?tipoHabitacionId=${tipoHabitacionId}`;
    }

    return this.http.get<Room[]>(url).pipe(
      map(response => {
        // La API devuelve un array directo según OpenAPI spec
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching rooms:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene una habitación por su ID
   */
  getRoom(id: number): Observable<Room | null> {
    return this.getRooms().pipe(
      map(rooms => rooms.find(r => r.id === id) || null)
    );
  }

  /**
   * Obtiene habitaciones con sus tipos y las convierte al formato de display
   */
  getRoomsForDisplay(): Observable<RoomDisplay[]> {
    return forkJoin({
      rooms: this.getRooms(),
      roomTypes: this.getRoomTypes()
    }).pipe(
      map(({ rooms, roomTypes }) => {

        // Crear un mapa de tipos de habitación para acceso rápido
        const roomTypeMap = new Map<number, RoomType>();
        roomTypes.forEach(type => roomTypeMap.set(type.id, type));

        // Si no hay habitaciones, mostrar los tipos de habitación
        if (rooms.length === 0 && roomTypes.length > 0) {
          return this.mapRoomTypesToDisplay(roomTypes);
        }

        // Convertir habitaciones al formato de display
        return this.mapRoomsToDisplay(rooms, roomTypeMap);
      }),
      catchError(error => {
        console.error('Error fetching rooms for display:', error);
        return of([]);
      })
    );
  }

  /**
   * Convierte TODAS las habitaciones al formato de display para el landing page
   * Mapea campos del OpenAPI:
   * - nombre → name
   * - precioPorNoche → price
   * - mainImageUrl → image
   * - significado → description
   */
  private mapRoomsToDisplay(rooms: Room[], roomTypeMap: Map<number, RoomType>): RoomDisplay[] {
    // Mostrar TODAS las habitaciones (no agrupar por tipo)
    return rooms.map(room => {
      const roomType = roomTypeMap.get(room.tipoHabitacionId);
      const mainImage = room.images?.find(img => img.isMain)?.imageUrl || room.images?.[0]?.imageUrl;

      return {
        id: room.id,
        name: room.nombre || roomType?.nombre || `Habitación ${room.numeroHabitacion}`,
        price: this.formatPrice(room.precioPorNoche),
        period: 'por noche',
        image: mainImage || '/Fotos/hotel-andino.jpg',
        size: `Piso ${room.piso}`,
        capacity: this.formatCapacity(roomType?.capacidad || 2),
        bed: this.getBedType(roomType?.capacidad || 2),
        description: roomType?.descripcion,
        significado: room.significado,
        allImages: room.images
      };
    });
  }

  /**
   * Convierte tipos de habitación al formato de display
   * Se usa cuando no hay habitaciones disponibles
   */
  private mapRoomTypesToDisplay(roomTypes: RoomType[]): RoomDisplay[] {
    return roomTypes.map(type => ({
      id: type.id,
      name: type.nombre,
      price: 'Consultar',
      period: 'por noche',
      image: '/Fotos/hotel-andino.jpg',
      size: '-',
      capacity: this.formatCapacity(type.capacidad),
      bed: this.getBedType(type.capacidad),
      description: type.descripcion
    }));
  }

  /**
   * Formatea el precio a formato peruano (Soles)
   */
  private formatPrice(price: number): string {
    if (!price || price === 0) return 'Consultar';
    return `S/. ${price.toFixed(0)}`;
  }

  /**
   * Formatea la capacidad de personas
   */
  private formatCapacity(capacidad: number): string {
    if (!capacidad) return '2 personas';
    return capacidad === 1 ? '1 persona' : `${capacidad} personas`;
  }

  /**
   * Determina el tipo de cama basado en la capacidad
   */
  private getBedType(capacidad: number): string {
    switch (capacidad) {
      case 1:
        return 'Cama individual';
      case 2:
        return 'Cama matrimonial';
      case 3:
        return '1 doble + 1 individual';
      case 4:
        return '2 camas matrimoniales';
      default:
        return `${Math.ceil(capacidad / 2)} camas`;
    }
  }

  /**
   * Verifica disponibilidad de habitaciones para fechas específicas
   * GET /rooms/availability
   * Query params: checkIn, checkOut, adults
   */
  checkRoomAvailability(checkIn: string, checkOut: string, adults: number): Observable<any> {
    const params = {
      checkIn,
      checkOut,
      adults: adults.toString()
    };

    return this.http.get<any>(`${this.apiUrl}/rooms/availability`, { params }).pipe(
      catchError(error => {
        console.error('Error checking room availability:', error);
        throw error;
      })
    );
  }

  /**
   * Mapea AvailableRoom (de búsqueda) a RoomDisplay (para lista)
   */
  mapAvailableRoomToDisplay(room: AvailableRoom, searchContext?: { checkIn: string, checkOut: string, adults: number, children: number }): RoomDisplay {
    return {
      id: room.id,
      name: room.nombre,
      price: this.formatPrice(room.precioTotal), // Mostrar precio total por la estadía
      period: 'total', // Indicar que es precio total
      image: room.foto || '/Fotos/hotel-andino.jpg',
      size: 'Disponible', // O usar otro campo si disponible
      capacity: this.formatCapacity(room.capacidadMaxima),
      bed: this.getBedType(room.capacidadMaxima),
      description: room.descripcion,
      significado: room.tipoHabitacion, // Usar tipo como subtítulo/significado
      allImages: room.allImages || (room.foto ? [{ id: 0, imageUrl: room.foto, isMain: true }] : []),
      searchContext: searchContext
    };
  }
  /**
   * Envia una reclamación (Libro de Reclamaciones)
   * POST /claims (endpoint simulado)
   */
  createClaim(claimData: any): Observable<any> {
    // Si no existe endpoint real, esto simulará el envío
    // return this.http.post(`${this.apiUrl}/claims`, claimData);

    // Simulación de éxito para demostración
    return of({ success: true, message: 'Reclamación registrada' });
  }

  /**
   * Obtiene la galería de imágenes del hotel desde el JSON local
   */
  getHotelGallery(): Observable<GalleryImage[]> {
    return this.http.get<GalleryImage[]>('/data/hotel-gallery.json').pipe(
      catchError(error => {
        console.error('Error fetching hotel gallery:', error);
        return of([]);
      })
    );
  }
}


